import React, { useCallback, useEffect, useRef, useState } from "react";
import { Bell, X, Check, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";

const KIND_LABELS = {
  "lcr.ready": "Legal Consultation Record",
  "lcr.updated": "Record Updated",
  "lcr.access_requested": "Access Request",
  "lcr.awaiting": "Record Awaiting",
};

/** Bell + dropdown that reads GET /me/notifications. */
export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/me/notifications");
      setItems(r.data.items || []);
      setUnread(r.data.unread_count || 0);
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll every 45s while open, every 90s otherwise
  useEffect(() => {
    const t = setInterval(load, open ? 45_000 : 90_000);
    return () => clearInterval(t);
  }, [open, load]);

  // Click outside to close
  useEffect(() => {
    const onDoc = (e) => { if (open && ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const markRead = async (id) => {
    setBusy(true);
    try { await api.post(`/me/notifications/${id}/read`); await load(); } finally { setBusy(false); }
  };
  const markAll = async () => {
    setBusy(true);
    try { await api.post(`/me/notifications/read-all`); await load(); } finally { setBusy(false); }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        data-testid="notification-bell"
        className="relative h-10 w-10 rounded-full bg-white border border-slate-200 hover:border-[#0A2540] grid place-items-center transition"
      >
        <Bell size={16} className="text-[#0A2540]" />
        {unread > 0 && (
          <span
            data-testid="notification-badge"
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[340px] max-h-[440px] bg-white border border-slate-200 rounded-2xl shadow-xl z-50 flex flex-col" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <p className="text-xs font-bold uppercase tracking-widest text-[#0A2540]">Notifications</p>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button onClick={markAll} disabled={busy} className="text-[11px] font-bold text-[#0A2540] hover:underline" data-testid="mark-all-read">
                  <CheckCheck size={14} className="inline"/> Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 text-slate-400 hover:text-[#0A2540]">
                <X size={14}/>
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {items.length === 0 && (
              <div className="p-8 text-center text-xs text-slate-400" data-testid="notifications-empty">
                No notifications yet
              </div>
            )}
            {items.map((n) => (
              <NotificationRow key={n.id} n={n} onRead={markRead} onClose={() => setOpen(false)} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationRow({ n, onRead, onClose }) {
  const kindLabel = KIND_LABELS[n.kind] || "Notification";
  const link = n.data?.deep_link;
  const content = (
    <div className={`px-4 py-3 border-b border-slate-100 ${n.read ? "bg-white" : "bg-[#FBF4DF]/60"} hover:bg-slate-50 transition`}>
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#D4AF37]">{kindLabel}</p>
          <p className="text-sm font-semibold text-[#0A2540] truncate">{n.title}</p>
          {n.body && <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{n.body}</p>}
          <p className="text-[10px] text-slate-400 mt-1">{(n.created_at || "").slice(0, 19).replace("T", " ")}</p>
        </div>
        {!n.read && (
          <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRead(n.id); }}
                  className="text-[10px] text-[#0A2540] hover:underline shrink-0" data-testid={`mark-read-${n.id}`}>
            <Check size={12} className="inline"/> Read
          </button>
        )}
      </div>
    </div>
  );
  if (link) {
    return (
      <Link to={link} onClick={() => { onRead(n.id); onClose(); }} data-testid={`notification-${n.id}`}>
        {content}
      </Link>
    );
  }
  return <div data-testid={`notification-${n.id}`}>{content}</div>;
}
